import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LockKeyhole, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user && typeof user === "object" && user.role === "admin") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await login(email.trim(), password);
    setSubmitting(false);
    if (result.ok) {
      toast.success("Signed in");
      navigate("/admin", { replace: true });
    } else {
      setError(result.error || "Login failed");
    }
  };

  return (
    <div className="min-h-[calc(100vh-73px)] flex items-center justify-center px-4 py-12 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm fade-up">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center mb-5">
            <LockKeyhole className="h-5 w-5" />
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 text-center">Admin sign in</h1>
          <p className="text-sm text-slate-500 text-center mt-1">
            Enter the admin credentials to manage your PDF library.
          </p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4" data-testid="admin-login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@pdflibrary.com"
                required
                autoComplete="username"
                className="h-11"
                data-testid="admin-login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="h-11"
                data-testid="admin-login-password"
              />
            </div>

            {error && (
              <div
                className="text-sm rounded-lg bg-rose-50 text-rose-700 border border-rose-200 px-3 py-2"
                data-testid="admin-login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-brand-accent hover:bg-brand-accent-hover text-brand-primary rounded-lg font-bold shadow-sm shadow-amber-500/20"
              data-testid="admin-login-submit"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Public visitors can browse the library without signing in.
        </p>
      </div>
    </div>
  );
}
